import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertCircle, CheckCircle, Mail, RefreshCw } from 'lucide-react';

export const EmailVerificationAlert: React.FC = () => {
  const { currentUser, isEmailVerified, sendVerificationEmail, checkEmailVerification } = useAuth();
  const [isResending, setIsResending] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [justVerified, setJustVerified] = useState(false);
  const [verificationFailed, setVerificationFailed] = useState(false);

  // Function to resend verification email
  const handleResendVerification = async () => {
    try {
      setIsResending(true);
      await sendVerificationEmail();
    } catch (error) {
      console.error('Error resending verification email:', error);
    } finally {
      setIsResending(false);
    }
  };

  // Modified manual refresh handler to show a message if verification failed
  const handleModifiedManualRefresh = async () => {
    if (!currentUser) return;

    try {
      setIsRefreshing(true);
      setVerificationFailed(false); // Reset the failed state

      // Use the checkEmailVerification function from AuthContext
      await checkEmailVerification();

      // Add a small delay to ensure the UI updates
      setTimeout(() => {
        setIsRefreshing(false);

        // If still not verified after checking, show the message
        if (!isEmailVerified) {
          setVerificationFailed(true);
        }
      }, 1000);
    } catch (error) {
      console.error('Error refreshing verification status:', error);
      setIsRefreshing(false);
      setVerificationFailed(true);
    }
  };

  // Check if the user just verified their email
  useEffect(() => {
    if (isEmailVerified && currentUser && !showSuccessMessage) {
      setShowSuccessMessage(true);
      setJustVerified(true);

      // Hide the success message after 5 seconds
      const timer = setTimeout(() => {
        setShowSuccessMessage(false);
      }, 5000);

      return () => clearTimeout(timer);
    }
    // Return a no-op cleanup function to ensure consistent hook behavior
    return () => {};
  }, [isEmailVerified, currentUser, showSuccessMessage]);

  // Don't show anything if no user or no email
  if (!currentUser || !currentUser.email) {
    return <></>;
  }

  // Show success message if email is verified and we want to show it
  if (isEmailVerified && showSuccessMessage) {
    return (
      <Alert className="mb-4 border-green-500 bg-green-50 dark:bg-green-950/20">
        <CheckCircle className="h-4 w-4 text-green-500" />
        <AlertTitle className="text-green-700 dark:text-green-400">Email verified!</AlertTitle>
        <AlertDescription className="text-green-700 dark:text-green-400">
          <p className="mb-2">
            Your email address has been successfully verified. You now have full access to
            VibeCraft!
          </p>
        </AlertDescription>
      </Alert>
    );
  }

  // Don't show warning if email is verified
  if (isEmailVerified) {
    return <></>;
  }

  // Show verification needed alert
  return (
    <Alert className="mb-4 border-amber-500 bg-amber-50 dark:bg-amber-950/20 py-2">
      <AlertCircle className="h-4 w-4 text-amber-500" />
      <AlertDescription className="text-amber-700 dark:text-amber-400 flex flex-col">
        <p className="text-sm text-center">Check your inbox for a verification link!</p>
        <div className="flex flex-wrap gap-2 mt-1 justify-center">
          <Button
            variant="outline"
            size="sm"
            onClick={handleResendVerification}
            disabled={isResending || isRefreshing}
            className="border-amber-500 text-amber-700 hover:bg-amber-100 hover:text-amber-800 dark:border-amber-700 dark:text-amber-400 dark:hover:bg-amber-900/30 h-8 px-3 py-1 text-xs"
          >
            {isResending ? (
              <>
                <Mail className="mr-1 h-3 w-3 animate-pulse" />
                Sending...
              </>
            ) : (
              <>
                <Mail className="mr-1 h-3 w-3" />
                Resend verification email
              </>
            )}
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={handleModifiedManualRefresh}
            disabled={isResending || isRefreshing}
            className="border-amber-500 text-amber-700 hover:bg-amber-100 hover:text-amber-800 dark:border-amber-700 dark:text-amber-400 dark:hover:bg-amber-900/30 h-8 px-3 py-1 text-xs"
          >
            {isRefreshing ? (
              <>
                <RefreshCw className="mr-1 h-3 w-3 animate-spin" />
                Checking...
              </>
            ) : (
              <>
                <RefreshCw className="mr-1 h-3 w-3" />
                I've verified my email
              </>
            )}
          </Button>
        </div>
        {verificationFailed && (
          <p className="mt-1 text-xs text-amber-700 dark:text-amber-400 text-center">
            Email not verified yet. Please check your inbox or spam folder.
          </p>
        )}
      </AlertDescription>
    </Alert>
  );
};

export default EmailVerificationAlert;
